import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/stackTypes';
import { getPricingGroups } from '../../services/pricing-group-service';
import { getPricingCodes } from '../../services/pricing-code-service';
import type { PricingGroup } from '../../models/pricing-group-model';
import type { PricingCode } from '../../models/pricing-code-model';
import { formatDiscountLabel, DISCOUNT_BADGE_COLORS } from '../../models/pricing-group-model';
import { isSuccessful } from '../../services/api-requests';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface ChargerPricingInfoProps {
  chargerId: string;
}

interface AppliedItem {
  id: string;
  name: string;
  type: 'group' | 'code';
  discountType: PricingGroup['discount_type'];
  discountValue: number;
  scope: PricingGroup['scope'];
}

export default function ChargerPricingInfo({ chargerId }: ChargerPricingInfoProps) {
  const navigation = useNavigation<NavigationProp>();
  const [items, setItems] = useState<AppliedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      const applied: AppliedItem[] = [];

      try {
        const [groupsRes, codesRes] = await Promise.all([
          getPricingGroups(),
          getPricingCodes(),
        ]);

        if (isSuccessful(groupsRes.status) && groupsRes.payload) {
          for (const g of groupsRes.payload.groups ?? []) {
            if (!g.is_active) continue;
            applied.push({
              id: g.id,
              name: g.name,
              type: 'group',
              discountType: g.discount_type,
              discountValue: g.discount_value,
              scope: g.scope,
            });
          }
        }

        if (isSuccessful(codesRes.status) && codesRes.payload) {
          for (const c of codesRes.payload.codes ?? []) {
            if (!c.is_active || c.is_revoked) continue;
            applied.push({
              id: c.id,
              name: c.name,
              type: 'code',
              discountType: c.discount_type,
              discountValue: c.discount_value,
              scope: c.scope,
            });
          }
        }
      } catch {
        // Silently fail — section simply won't show items
      }

      if (!cancelled) {
        setItems(applied);
        setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [chargerId]);

  const handlePress = (item: AppliedItem) => {
    if (item.type === 'group') {
      navigation.navigate('PricingGroupDetail', { groupId: item.id });
    } else {
      navigation.navigate('PricingCodeDetail', { codeId: item.id });
    }
  };

  return (
    <View style={styles.section}>
      <View style={styles.headerRow}>
        <Text style={styles.headerText}>Applied Pricing</Text>
        <MaterialIcons name="info-outline" size={18} color="#8D8E90" />
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="small" color="#40BFB4" />
        </View>
      ) : items.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialIcons name="price-change" size={32} color="#8D8E90" />
          <Text style={styles.emptyText}>
            No pricing groups or codes apply to this charger.
          </Text>
        </View>
      ) : (
        items.map((item) => {
          const badgeColor = DISCOUNT_BADGE_COLORS[item.discountType];
          return (
            <Pressable
              key={`${item.type}-${item.id}`}
              style={({ pressed }) => [styles.itemRow, { opacity: pressed ? 0.8 : 1 }]}
              onPress={() => handlePress(item)}
            >
              <View style={[styles.badge, { backgroundColor: badgeColor + '20' }]}>
                <Text style={[styles.badgeText, { color: badgeColor }]}>
                  {formatDiscountLabel(item.discountType, item.discountValue)}
                </Text>
              </View>

              <View style={styles.itemContent}>
                <Text style={styles.itemName} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.itemMeta}>
                  {item.type === 'group' ? 'Group' : 'Code'}
                  {' · '}
                  {item.scope === 'all_host_chargers' ? 'All chargers' : 'Specific'}
                </Text>
              </View>

              <MaterialIcons name="chevron-right" size={22} color="#8D8E90" />
            </Pressable>
          );
        })
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: 16,
    marginBottom: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  headerText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  centered: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyContainer: {
    backgroundColor: '#292D33',
    borderRadius: 10,
    padding: 24,
    alignItems: 'center',
    gap: 10,
  },
  emptyText: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 18,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#292D33',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    gap: 12,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  itemContent: {
    flex: 1,
  },
  itemName: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
    marginBottom: 2,
  },
  itemMeta: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
  },
});
